import * as React from "react"
import { type EntityCategory } from "@digits-graphql/frontend/graphql-bearer"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import colors from "@digits-shared/themes/colors"
import { DetailText, LabelText } from "@digits-shared/themes/typography"
import styled from "styled-components"
import { BULK_FIELD_WIDTH } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/types"
import { CategoryTypeahead } from "src/shared/components/Typeahead/CategoryTypeahead"

const InputGroup = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`

const StyledCategoryTypeahead = styled(CategoryTypeahead)`
  --category-typeahead-input-width: ${BULK_FIELD_WIDTH}px;
  height: 32px;
`

export const BulkActionsCategoryTypeahead: React.FC<{
  category: EntityCategory | undefined
  setCategory: (category: EntityCategory | undefined) => void
  placeholder: string | undefined
  disabled: boolean
  customLabel?: string
}> = ({ category, setCategory, placeholder, disabled, customLabel }) => {
  const onSelect = React.useCallback(
    (c: EntityCategory) => {
      setCategory(c)
    },
    [setCategory]
  )

  const onDelete = React.useCallback(() => {
    setCategory(undefined)
  }, [setCategory])

  return (
    <InputGroup>
      <LabelText>{customLabel ? customLabel : "Source Category"}</LabelText>

      <StyledCategoryTypeahead
        category={category}
        onSelect={onSelect}
        onDelete={onDelete}
        disabled={disabled}
        hideIcons={!category}
        inputPlaceholder={placeholder}
        disabledInputPlaceholder={placeholder}
      />
    </InputGroup>
  )
}

export const BulkActionsSplitCategoryTypeahead: React.FC<{
  category: EntityCategory | undefined
  setCategory: (category: EntityCategory | undefined) => void
  placeholder: string | undefined
  disabled: boolean
  hasTransactionWithMultipleSplit: boolean
  customLabel?: string
}> = ({
  category,
  setCategory,
  placeholder,
  disabled,
  hasTransactionWithMultipleSplit,
  customLabel,
}) => {
  const onSelect = React.useCallback(
    (c: EntityCategory) => {
      setCategory(c)
    },
    [setCategory]
  )

  const onDelete = React.useCallback(() => {
    setCategory(undefined)
  }, [setCategory])

  return (
    <InputGroup>
      <LabelText>{customLabel ? customLabel : "Related Category"}</LabelText>

      <DigitsTooltip
        content={hasTransactionWithMultipleSplit && <MultipleSplitCategoryTooltipContent />}
        disabled={!hasTransactionWithMultipleSplit}
        maxWidth={296}
      >
        <StyledCategoryTypeahead
          category={category}
          onSelect={onSelect}
          onDelete={onDelete}
          disabled={disabled}
          hideIcons={!category}
          inputPlaceholder={placeholder}
          disabledInputPlaceholder={placeholder}
        />
      </DigitsTooltip>
    </InputGroup>
  )
}

const MultipleSplitCategoryTooltipContent: React.FC = () => (
  <div>
    <DetailText color={colors.white} weight="heavy">
      Bulk editing not available
    </DetailText>
    <DetailText color={colors.translucentWhite70}>
      Transactions with more than 2 lines can only be edited from transaction details.
    </DetailText>
  </div>
)
